/**
 * Utilidades para mostrar alertas y diálogos usando SweetAlert2
 */
import Swal from 'sweetalert2';

// Configuración por defecto para las alertas
const defaultConfig = {
  confirmButtonColor: '#3085d6',
  cancelButtonColor: '#d33',
  confirmButtonText: 'Aceptar',
  cancelButtonText: 'Cancelar',
  reverseButtons: true,
  allowOutsideClick: false,
  buttonsStyling: true,
  showConfirmButton: true,
  showClass: { popup: 'swal-show' },
  hideClass: { popup: 'swal-hide' },
  customClass: {
    confirmButton: 'swal2-confirm-button',
    cancelButton: 'swal2-cancel-button'
  }
};

/**
 * Muestra una alerta de éxito
 * @param {string} title - Título de la alerta
 * @param {string} text - Texto descriptivo (opcional)
 * @param {Object} options - Opciones adicionales para la alerta
 */
export const swalSuccess = (title, text = '', options = {}) => {
  return Swal.fire({
    icon: 'success',
    title,
    text,
    ...defaultConfig,
    ...options,
  });
};

/**
 * Muestra una alerta de error
 * @param {string} title - Título de la alerta
 * @param {string} text - Texto descriptivo (opcional)
 * @param {Object} options - Opciones adicionales para la alerta
 */
export const swalError = (title, text = '', options = {}) => {
  return Swal.fire({
    icon: 'error',
    title,
    text,
    ...defaultConfig,
    ...options,
  });
};

/**
 * Muestra una alerta informativa
 * @param {string} title - Título de la alerta
 * @param {string} text - Texto descriptivo (opcional)
 * @param {Object} options - Opciones adicionales para la alerta
 */
export const swalInfo = (title, text = '', options = {}) => {
  return Swal.fire({
    icon: 'info',
    title,
    text,
    ...defaultConfig,
    ...options,
  });
};

/**
 * Muestra una alerta de advertencia
 * @param {string} title - Título de la alerta
 * @param {string} text - Texto descriptivo (opcional)
 * @param {Object} options - Opciones adicionales para la alerta
 */
export const swalWarning = (title, text = '', options = {}) => {
  return Swal.fire({
    icon: 'warning',
    title,
    text,
    ...defaultConfig,
    ...options,
  });
};

/**
 * Muestra un diálogo de confirmación
 * @param {string} title - Título del diálogo
 * @param {string} text - Texto descriptivo (opcional)
 * @param {string} confirmButtonText - Texto del botón de confirmación (opcional)
 * @param {Object} options - Opciones adicionales para el diálogo
 * @returns {Promise} - Promesa que se resuelve con el resultado del diálogo
 */
export const swalConfirm = (title, text = '', confirmButtonText = 'Sí', options = {}) => {
  return Swal.fire({
    icon: 'warning',
    title,
    text,
    showCancelButton: true,
    confirmButtonText,
    showDenyButton: false,
    denyButtonText: undefined,
    input: undefined,
    inputAttributes: undefined,
    inputPlaceholder: undefined,
    showLoaderOnConfirm: false,
    ...defaultConfig,
    ...options,
  });
};

/**
 * Muestra un diálogo con input para que el usuario ingrese datos
 * @param {string} title - Título del diálogo
 * @param {string} inputPlaceholder - Placeholder para el input
 * @param {string} inputType - Tipo de input (text, email, password, etc.)
 * @param {Object} options - Opciones adicionales para el diálogo
 * @returns {Promise} - Promesa que se resuelve con el valor ingresado
 */
export const swalInput = (title, inputPlaceholder = '', inputType = 'text', options = {}) => {
  const { didOpen: callerDidOpen, customClass: callerCustomClass, ...restOptions } = options;
  return Swal.fire({
    title,
    input: inputType,
    inputPlaceholder,
    showCancelButton: true,
    ...defaultConfig,
    ...restOptions,
    customClass: {
      confirmButton: 'swal2-confirm-button',
      cancelButton: 'swal2-cancel-button',
      ...(callerCustomClass || {}),
    },
    didOpen: (popup) => {
      // Forzar visibilidad solo del <input> — el CSS base lo oculta por defecto
      const inputEl = popup.querySelector('.swal2-input');
      if (inputEl) inputEl.style.setProperty('display', 'block', 'important');
      if (callerDidOpen) callerDidOpen(popup);
    },
  });
};

/**
 * Muestra un diálogo con múltiples pasos (wizard)
 * @param {Array} steps - Array de objetos con la configuración de cada paso
 * @param {Object} options - Opciones adicionales para el diálogo
 * @returns {Promise} - Promesa que se resuelve con los resultados de todos los pasos
 */
export const swalSteps = async (steps, options = {}) => {
  const results = [];
  
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const result = await Swal.fire({
      title: step.title,
      html: step.html || '',
      input: step.input || null,
      inputPlaceholder: step.inputPlaceholder || '',
      currentProgressStep: i,
      progressSteps: Array.from({ length: steps.length }, (_, i) => i),
      ...defaultConfig,
      ...options,
      ...step.options,
    });
    
    if (result.isDismissed) {
      return { completed: false, results };
    }
    
    results.push(result.value);
  }
  
  return { completed: true, results };
};

/**
 * Muestra una alerta con temporizador de auto-cierre
 * @param {string} title - Título de la alerta
 * @param {string} text - Texto descriptivo (opcional)
 * @param {number} timer - Tiempo en milisegundos antes de cerrar automáticamente
 * @param {Object} options - Opciones adicionales para la alerta
 */
export const swalTimer = (title, text = '', timer = 2000, options = {}) => {
  return Swal.fire({
    icon: 'info',
    title,
    text,
    timer,
    timerProgressBar: true,
    ...defaultConfig,
    ...options,
  });
};

/**
 * Muestra una alerta con contenido HTML personalizado
 * @param {string} title - Título de la alerta
 * @param {string} htmlContent - Contenido HTML
 * @param {Object} options - Opciones adicionales para la alerta
 */
export const swalHtml = (title, htmlContent, options = {}) => {
  return Swal.fire({
    title,
    html: htmlContent,
    ...defaultConfig,
    ...options,
  });
};

/**
 * Actualiza la configuración por defecto de las alertas
 * @param {Object} newConfig - Nueva configuración por defecto
 */
export const settingsSwal = (newConfig = {}) => {
  Object.assign(defaultConfig, newConfig);
};

/**
 * Cierra todas las alertas abiertas
 */
export const swalClose = () => {
  Swal.close();
};
